import { useEffect, useState } from 'react';

import useDebounce from 'hooks/useDebounce';
import useUpdateEffect from 'hooks/useUpdateEffect';

import * as S from './styled/Collapse.styled';

/**
 * @author Burak Unsal
 * @description Collapse component is used to show/hide content.
 * @param {Object} props - props of the component.
 * @param {object} props.buttonRef - Ref of the button that will be clicked to show/hide content.
 * @param {boolean} props.visible - If you want to control the collapse component from outside, you can use this prop.
 * @param {boolean} props.defaultVisible - If you want to set the default state of collapse component, you can use this prop.
 * @param {boolean} props.invisibleMode - If you want to hide the collapse component when it is closed, you can use this prop.
 * @param {node} props.children - You can send component, text, so everything.
 * @returns Collapse component
 * @example
 * <Collapse buttonRef={ref} defaultVisible>
 * 	<Content />
 * </Collapse>
 * @example
 * <Collapse visible={visible} defaultVisible>
 * 	<Content />
 * </Collapse>
 */
const Collapse = ({ buttonRef, visible, defaultVisible, invisibleMode, children }) => {
	const [open, setOpen] = useState(visible ?? defaultVisible ?? false);
	const [active, setActive] = useState(visible ?? defaultVisible ?? false);
	const [animate, setAnimate] = useState(false);
	const [containerRef, setContainerRef] = useState(null);

	/**
	 * @description - Debounced height value of the container
	 */
	const debouncedContainerHeight = useDebounce(containerRef?.scrollHeight ?? 0, 100);

	/**
	 * @description - When animation is finished, unblock the button click and set the active and open state.
	 */
	useEffect(() => {
		if (animate) {
			setActive(!active);
			if (!active) {
				setOpen(true);
			}
		} else {
			if (buttonRef?.current) {
				buttonRef.current.style.pointerEvents = 'auto';
			}
			setOpen(active);
		}
	}, [animate]);

	/**
	 * @description - When collapse container is opened, add 'expanded' class to button.
	 */
	useEffect(() => {
		if (!buttonRef?.current) {
			return;
		}

		if (active) {
			buttonRef.current.classList.add('expanded');
		} else {
			buttonRef.current.classList.remove('expanded');
		}
	}, [active]);

	/**
	 * @description - Add click event listener to button. When button is clicked, set animate to true and block button click event.
	 */
	useEffect(() => {
		const handleToggle = () => {
			if (open !== active) return;
			if (buttonRef?.current) {
				buttonRef.current.style.pointerEvents = 'none';
			}
			setAnimate(true);
		};

		buttonRef?.current?.addEventListener('click', handleToggle);

		return () => {
			buttonRef?.current?.removeEventListener('click', handleToggle);
		};
	}, [buttonRef?.current, open, active]);

	/**
	 * @description - Add transitionend event listener to container. When the transition is complete, set animate to false.
	 */
	useEffect(() => {
		const handleTransitionEnd = () => {
			setAnimate(false);
		};

		containerRef?.addEventListener('transitionend', handleTransitionEnd);

		return () => {
			containerRef?.removeEventListener('transitionend', handleTransitionEnd);
		};
	}, [containerRef]);

	/**
	 * @description - When visible prop changes, set animate to true.
	 */
	useUpdateEffect(() => {
		if (visible !== undefined && visible !== open) {
			if (animate === false) {
				setAnimate(true);
			} else {
				setAnimate(false);
				setOpen(visible);
				setActive(visible);
			}
		}
	}, [visible]);

	/**
	 * @description - Set the max-height of the container based on the animate state.
	 */
	const style = animate
		? {
				transition: 'max-height 0.5s ease',
				maxHeight: `${active ? debouncedContainerHeight : 0}px`
			}
		: undefined;

	return (
		<>
			{(open || invisibleMode) && (
				<S.Container
					ref={setContainerRef}
					style={style}
					className="collapse-container"
					invisible={invisibleMode && !open && !animate}
					animate={animate}>
					{children}
				</S.Container>
			)}
		</>
	);
};

export default Collapse;
